// Auth store — setup-style (doc 04 §6.2). Token persisted to localStorage.
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { authApi } from '@/services/api/auth.api'
import type { LoginCredentials, RegisterPayload, User } from '@/types/auth'
import type { Player } from '@/types/player'
import type { RoleName } from '@/types/enums'

const TOKEN_KEY = 'cpa.token'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const player = ref<Player | null>(null)
  const token = ref<string | null>(localStorage.getItem(TOKEN_KEY))
  const roles = ref<RoleName[]>([])
  const permissions = ref<string[]>([])

  const isAuthenticated = computed(() => !!user.value)
  const isAdmin = computed(() =>
    hasAnyRole(['super-admin', 'association-admin']),
  )

  function hasRole(role: RoleName): boolean {
    return roles.value.includes(role)
  }
  function hasAnyRole(req: RoleName[]): boolean {
    return req.some((r) => roles.value.includes(r))
  }
  function can(perm: string): boolean {
    return permissions.value.includes(perm)
  }

  function setToken(next: string | null) {
    token.value = next
    if (next) {
      localStorage.setItem(TOKEN_KEY, next)
    } else {
      localStorage.removeItem(TOKEN_KEY)
    }
  }

  async function login(credentials: LoginCredentials): Promise<User> {
    const result = await authApi.login(credentials)
    setToken(result.token)
    await fetchMe()
    return result.user
  }

  async function register(payload: RegisterPayload): Promise<User> {
    const result = await authApi.register(payload)
    setToken(result.token)
    await fetchMe()
    return result.user
  }

  async function fetchMe(): Promise<void> {
    const me = await authApi.me()
    // `/auth/me` returns the user flattened onto `data` (roles/permissions/player
    // nested in), so `me` itself is the user.
    user.value = me
    player.value = me.player
    roles.value = me.roles
    permissions.value = me.permissions
  }

  /**
   * Re-hydrate the session on app boot: if a token was persisted but the user
   * isn't loaded yet (e.g. after a hard refresh), fetch the profile. On failure
   * (expired/invalid token) the session is cleared so the app falls back to guest.
   */
  async function restore(): Promise<void> {
    if (token.value && !user.value) {
      try {
        await fetchMe()
      } catch {
        clearSession()
      }
    }
  }

  async function logout(): Promise<void> {
    try {
      await authApi.logout()
    } finally {
      clearSession()
    }
  }

  function clearSession() {
    user.value = null
    player.value = null
    roles.value = []
    permissions.value = []
    setToken(null)
  }

  return {
    user,
    player,
    token,
    roles,
    permissions,
    isAuthenticated,
    isAdmin,
    hasRole,
    hasAnyRole,
    can,
    login,
    register,
    fetchMe,
    restore,
    logout,
    clearSession,
  }
})
